import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { MdMenu, MdClose, MdExitToApp, MdExpandMore, MdExpandLess } from 'react-icons/md';
import OverdueCreditAlert from './OverdueCreditAlert';
import ErrorBoundary from '../common/ErrorBoundary';
import { PageSpinner } from '../common';
import { visibleNav, activeGroup, readOpenGroups, writeOpenGroups } from '../../config/navigation';

export default function AppLayout() {
  const { user, logout, isAdmin, isDEO, canManageAll } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const handleLogout = () => { logout(); navigate('/login'); };
  const nav = visibleNav({ isAdmin, isDEO, canManageAll, reportPermissions: user?.reportPermissions });

  // Remembered open groups, plus the group holding the current page.
  const [openGroups, setOpenGroups] = useState<string[]>(readOpenGroups);
  const current = activeGroup(nav, pathname);
  useEffect(() => {
    if (current) setOpenGroups(p => (p.includes(current) ? p : [...p, current]));
  }, [current]);
  useEffect(() => { writeOpenGroups(openGroups); }, [openGroups]);
  const toggleGroup = (label: string) =>
    setOpenGroups(p => p.includes(label) ? p.filter(l => l !== label) : [...p, label]);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5" style={{ borderBottom:'1px solid rgba(255,255,255,.1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center text-p-deep font-bold text-sm flex-shrink-0"
            style={{ background:'rgba(255,255,255,.9)', borderRadius:'var(--r-sm)' }}>WI</div>
          <div>
            <div className="text-white font-semibold text-sm leading-tight">Watson International</div>
            <div className="text-xs mt-0.5" style={{ color:'rgba(255,255,255,.45)' }}>Quotation System</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {nav.map(item => {
          // Group item (has children)
          if (item.children) {
            const visibleChildren = item.children;
            const isGroupOpen = openGroups.includes(item.label);
            return (
              <div key={item.label}>
                <button onClick={() => toggleGroup(item.label)} aria-expanded={isGroupOpen}
                  className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all"
                  style={{ borderRadius: 'var(--r-sm)' }}>
                  <div className="flex items-center gap-3">
                    <item.icon size={18} className="flex-shrink-0" />
                    {item.label}
                  </div>
                  {isGroupOpen
                    ? <MdExpandLess size={16} />
                    : <MdExpandMore size={16} />}
                </button>
                {isGroupOpen && (
                  <div className="ml-3 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                    {visibleChildren.map(child => (
                      <NavLink key={child.to + child.label} to={child.to} end={child.end} onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-all ${
                            isActive
                              ? 'text-p-deep'
                              : 'text-white/50 hover:text-white hover:bg-white/10'
                          }`
                        }
                        style={({ isActive }) => ({
                          borderRadius: 'var(--r-sm)',
                          ...(isActive ? { background: 'rgba(255,255,255,.92)' } : {}),
                        })}>
                        <child.icon size={16} className="flex-shrink-0" />
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Single item
          return (
            <NavLink key={item.to} to={item.to!} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'text-p-deep'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`
              }
              style={({ isActive }) => ({
                borderRadius: 'var(--r-sm)',
                ...(isActive ? { background: 'rgba(255,255,255,.92)' } : {}),
              })}>
              <item.icon size={18} className="flex-shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* User */}
      <div className="px-3 py-4" style={{ borderTop:'1px solid rgba(255,255,255,.1)' }}>
        <div className="flex items-center gap-3 px-3 py-2 mb-2" style={{ background:'rgba(255,255,255,.1)', borderRadius:'var(--r-sm)' }}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-p-deep text-xs font-bold flex-shrink-0"
            style={{ background:'rgba(255,255,255,.9)' }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-xs font-medium truncate">{user?.name}</div>
            <div className="text-xs capitalize" style={{ color:'rgba(255,255,255,.45)' }}>{user?.role}</div>
          </div>
        </div>
        <button onClick={handleLogout}
          className="flex w-full items-center gap-3 px-3 py-2 text-sm font-medium transition-all rounded-sm"
          style={{ color:'rgba(255,255,255,.55)', borderRadius:'var(--r-sm)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,.1)'; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background=''; (e.currentTarget as HTMLButtonElement).style.color='rgba(255,255,255,.55)'; }}>
          <MdExitToApp size={18} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background:'var(--canvas)' }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-56 flex-shrink-0"
        style={{ background:'linear-gradient(160deg, var(--p-deep) 0%, var(--p) 100%)' }}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-56 flex flex-col z-10"
            style={{ background:'linear-gradient(160deg, var(--p-deep) 0%, var(--p) 100%)' }}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4" style={{ color:'rgba(255,255,255,.6)' }}>
              <MdClose size={20} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 text-white"
          style={{ background:'linear-gradient(90deg, var(--p-deep), var(--p))' }}>
          <button onClick={() => setOpen(true)}><MdMenu size={24} /></button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded flex items-center justify-center font-bold text-xs text-p-deep"
              style={{ background:'rgba(255,255,255,.9)', borderRadius:'var(--r-xs)' }}>WI</div>
            <span className="font-semibold text-sm">Watson International</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <RouteContent />
        </main>
      </div>
      {canManageAll && <ErrorBoundary><OverdueCreditAlert /></ErrorBoundary>}
    </div>
  );
}

function RouteContent() {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}><Suspense fallback={<PageSpinner />}><Outlet /></Suspense></ErrorBoundary>;
}
